import React, { useCallback, useContext, useMemo } from "react";
import { HeaderB } from "components/headers";
import { FragmentA, ModuleWrapper } from "components/fragments";
import { CheckboxField, Field, Input, Select } from "components/commons";
import { Transfer } from "antd";
import { ActionButton, Form, Panel, Toast } from "components/commons";
import { useApi, useForm, useModal, useMount, useRouter } from "hooks";
import { VenueContext } from "contexts";
import { Path } from "paths";
import initialKdsFormState from "./kds.form-state";
import { mapObject, mapObjectsToSelect } from "services";
import { kdsMappingRequest, allLocationRequest, location as locationMapper } from "mappers";
// import { searchCategory } from "apis/category.api";
import { searchProduct } from "apis/product.api";
// import { searchProductSku } from "apis/product.api";
import { searchLocation } from "apis";

const KdsForm = ({ title, initialState, loading, submitting, submit }) => {
  const { venue } = useContext(VenueContext);
  const { venueId } = venue;
  const { history } = useRouter();
  const unsaveChangesModal = useModal();

  const formState = useMemo(() => initialKdsFormState(initialState), [initialState]);
  const { fields, modifyField, submitForm, getFormValues, dirty } = useForm({
    initialState: formState,
  });

  const {
    request: outletRequest,
    mappedData: outlets = [],
    loading: outletLoading,
  } = useApi({
    api: searchLocation,
    isArray: true,
    mapper: locationMapper,
    handleOwnError: true,
  });

  // const { request: loadCategories, loading: categoriesLoading } = useApi({ api: searchCategory });
  const { request: loadProducts, loading: productsLoading } = useApi({ api: searchProduct });
  // const { request: loadSkus, loading: skusLoading } = useApi({ api: searchProductSku });

  // const [categoryOptions, setCategoryOptions] = React.useState([]);
  const [productOptions, setProductOptions] = React.useState([]);
  // const [skuOptions, setSkuOptions] = React.useState([]);

  const outletOptions = useMemo(
    () => mapObjectsToSelect(
      [...outlets].sort((a, b) => a.name.localeCompare(b.name)),
      { textKey: "name", valueKey: "id" }
    ),
    [outlets]
  );

  const transferDataSource = useMemo(
    () => productOptions.map((o) => ({ key: String(o.value), title: o.text })),
    [productOptions]
  );

  // const fetchCategories = useCallback(async () => {
  //   const res = await loadCategories({ venueId, page: 1, pageSize: 1000, hasProduct: false });
  //   const options = mapObjectsToSelect(
  //     (res?.data || []).map((item) => ({ id: item.categoryId, value: item.categoryName })),
  //     { valueKey: "id", textKey: "value" }
  //   );
  //   setCategoryOptions(options);
  // }, [loadCategories, venueId]);

  const fetchProducts = useCallback(async (outletId) => {
    if (!outletId) {
      setProductOptions([]);
      return;
    }
    const res = await loadProducts({
      venueId,
      page: 1,
      pageSize: 1000,
      searchKey: "",
      locationIds: String(outletId),
      categoryIds: "",
      productStockLevels: "",
      orders: { "p.createdDate": "desc" },
    });
    const options = mapObjectsToSelect(
      (res?.data || []).map((item) => ({ id: item.productId, value: item.productName })),
      { valueKey: "id", textKey: "value" }
    );
    setProductOptions(options);
  }, [loadProducts, venueId]);

  // const fetchSkus = useCallback(async () => {
  //   const res = await loadSkus({ venueId, page: 1, pageSize: 1000 });
  //   const options = mapObjectsToSelect(
  //     (res?.data || []).map((item) => ({
  //       id: item.productSkuId,
  //       value: item.sku || `#${item.productSkuId}`,
  //     })),
  //     { valueKey: "id", textKey: "value" }
  //   );
  //   setSkuOptions(options);
  // }, [loadSkus, venueId]);

  useMount(() => {
    outletRequest(mapObject({ venueId }, allLocationRequest));
    // fetchCategories();
    // fetchSkus();
    if (initialState?.outletId) {
      fetchProducts(initialState.outletId);
    }
  });

  const goToList = useCallback(() => {
    history.push(Path.LOCATION_KDS);
  }, [history]);

  const leavePage = useCallback(() => {
    if (dirty) {
      unsaveChangesModal.show({
        ok: () => {
          goToList();
          unsaveChangesModal.close();
        },
      });
      return;
    }
    goToList();
  }, [dirty, goToList, unsaveChangesModal]);

  const handleSubmit = useCallback(async () => {
    const values = getFormValues();
    const mapping = mapObject(values, kdsMappingRequest);
    const res = await submit({ ...values, ...mapping, venueId });
    Toast({ content: res?.message || "KDS saved", success: true, icon: "check" }).open();
    goToList();
  }, [getFormValues, goToList, submit, venueId]);

  return (
    <ModuleWrapper
      loading={loading}
      header={<HeaderB title={title} returnText="KDS" onClick={leavePage} />}
    >
      <Form unsaveChangesModal={unsaveChangesModal} onSubmit={() => submitForm(handleSubmit)} isPrompt={dirty}>
        <FragmentA title="Basic Info">
          <Panel>
            <Field label={fields.name.label} {...fields.name}>
              <Input {...fields.name} onChange={modifyField} />
            </Field>
            <Field label={fields.outletId.label} {...fields.outletId}>
              <Select
                {...fields.outletId}
                options={outletOptions}
                loading={outletLoading}
                placeholder="Select outlet"
                onChange={(name, { value }) => {
                  modifyField(name, { value });
                  fetchProducts(value);
                }}
              />
            </Field>
            <Field label={fields.type.label} {...fields.type}>
              <Select {...fields.type} onChange={modifyField} />
            </Field>
            <Field label={fields.status.label} {...fields.status}>
              <Select {...fields.status} onChange={modifyField} />
            </Field>
            <Field label={fields.isDefault.label} {...fields.isDefault}>
              <CheckboxField {...fields.isDefault} onChange={modifyField} />
            </Field>
          </Panel>
        </FragmentA>
        <FragmentA title="Mappings">
          <Panel>
            {/* TODO: re-enable categories
            <Field label={fields.categories.label} {...fields.categories}>
              <MultipleSelect
                {...fields.categories}
                onChange={modifyField}
                options={categoryOptions}
                optionsLoading={categoriesLoading}
                selectAllText="All Categories"
                placeholder="Select categories"
              />
            </Field>
            */}
            <div className="mb-md">
              <p className="text-sm font-medium mb-xs">{fields.products.label}</p>
              <Transfer
                dataSource={transferDataSource}
                targetKeys={fields.products.value || []}
                onChange={(nextTargetKeys) => modifyField("products", { value: nextTargetKeys })}
                render={(item) => item.title}
                titles={["Available", "Mapped"]}
                showSearch
                loading={productsLoading}
                listStyle={{ width: "100%", height: 320 }}
                locale={{ itemUnit: "product", itemsUnit: "products", searchPlaceholder: "Search" }}
              />
            </div>
            {/* TODO: re-enable skus
            <Field label={fields.skus.label} {...fields.skus}>
              <MultipleSelect
                {...fields.skus}
                onChange={modifyField}
                options={skuOptions}
                optionsLoading={skusLoading}
                selectAllText="All Skus"
                placeholder="Select skus"
              />
            </Field>
            */}
          </Panel>
        </FragmentA>
        <ActionButton
          showLine
          loading={submitting}
          primary={{ onClick: () => submitForm(handleSubmit) }}
          secondary={{ onClick: leavePage }}
        />
      </Form>
    </ModuleWrapper>
  );
};

export default KdsForm;
